import { ref, computed, watch, type Ref } from 'vue'
import type { components } from '@/api/schema'
import type { useJobs } from '@/composables/useJobs'
import { rowFromStep, type VisitRow } from '@/lib/visitRows'
import { commitmentAttributes, type AgreementDraft } from '@/lib/commitment'
import type { TemplateLineInput } from '@/composables/useTemplatePricing'

type TemplateStep = components['schemas']['TemplateStepResource']
type CreateInput = Parameters<ReturnType<typeof useJobs>['create']>[0]

// ★ THE FOUR STATES OF A DEAL AT THE END OF THE CALL (design 165). The intake
// asks WHERE THE DEAL IS, and every property of the job is filled on its page
// afterwards — with ONE exception, the price of a deal already won, because a
// job must be BORN in its state (his ruling): a page that opens on a door is
// a job created wrong and patched on arrival.
//
//   lead       — « Rien de décidé »: the container alone, no visit. The route
//                out (a look-see, the paper) is a later verb on the page.
//   evaluation — « Il faut aller voir »: one flexible look-see lands in the
//                bank. The type's default when it says so.
//   quote      — « À rédiger, sans visite »: born assessed, zero visits, the
//                paper is owed and written on the page.
//   won        — « Le client a dit oui »: the card unfolds « Combien ? »
//                (offer · amount · no paper) and the POST carries the answer
//                — the server mints the accepted paper and approves in the
//                same transaction, so the job lands on its page as Travaux.
//
// ⭐ THE RULE THE FOUR ENFORCE: visits exist only past a commitment — the
// look-see the évaluation card mints, approval's seeding, a deliberate
// « Réserver ». Never as an intake side effect on an unsigned deal.
export type IntakeChoice = 'lead' | 'evaluation' | 'quote' | 'won'
export const INTAKE_CHOICES: readonly IntakeChoice[] = [ 'lead', 'evaluation', 'quote', 'won' ]

// The card the TYPE preselects: its « évaluation par défaut » flag says whether
// someone must see the site first. No type yet ⇒ évaluation (seeing is never
// wrong); a flag that says no ⇒ the paper is written without a visit.
export const defaultIntakeFor = (evaluationByDefault: boolean | null | undefined): IntakeChoice =>
  (evaluationByDefault === false ? 'quote' : 'evaluation')

export interface IntakePrefills {
  initialIntake?: IntakeChoice
  initialTemplateId?: string
  originJobId?: string
}

// What a host learns at creation (both surfaces): the card the job was born
// under, the type the conversation settled on, and the one line to toast.
export type JobCreated = { intake: IntakeChoice; templateId?: string; notice: string }

export type IntakeCreateAttributes = Pick<CreateInput,
  'lead_capture' | 'work_scoped' | 'skip_proposal' | 'visits' | 'work_period_id' | 'season_year' | 'won_line' | 'won_template'>

// « Combien ? » as the « Vendu » card asks it: the draft the form holds, plus
// the template face's data half (its lines at the entered numbers and the
// chosen offer), reported by CommitmentFields.
export type IntakeAgreement = {
  draft: AgreementDraft
  templateLines?: TemplateLineInput[]
  proposalTemplateId?: string | null
}

// THE SEASON THE JOB IS FOR, as the intake line shows it: the type's default
// (served on the template) until the dispatcher flips it. Null = the type has
// no season, and the line is not drawn.
export type IntakeSeason = { workPeriodId: string; seasonYear: number; label: string }

// The shared half of quick-capture — the intake DECISION state and the lane
// part of the POST /jobs body, for both surfaces (console JobCreateForm,
// handheld FieldJobCreateForm). Headless law: the markup differs per surface,
// the rules must not. This owns which card is chosen, how the type steers it,
// the branch provenance, and the rows a sales-off shop seeds from the type.
// ★ Deliberately PURE: no API composable, no useI18n, no component imports — so
// it is callable outside a setup context and unit-testable on its own. The
// forms own the FETCHING (they hand `seedFromTemplate` the steps and the flag
// they fetched); this owns only the rules the two surfaces must not be able to
// disagree about.
export function useJobIntake(prefills: IntakePrefills, guardLocationId: Ref<string | null>) {
  // A prefilled card (a spawn door that already knows) is the starting answer;
  // otherwise the type's flag picks it the moment a type is chosen — and only
  // until someone answers. An ANSWERED card (the door's, or a hand pick through
  // `pickIntake`) survives every later type change: the flag is a default, not
  // a veto, and a card that flips under a dispatcher who already chose is a
  // reset they did not ask for.
  const intake = ref<IntakeChoice>(prefills.initialIntake ?? 'evaluation')
  const answered = ref(prefills.initialIntake !== undefined)

  const pickIntake = (choice: IntakeChoice) => {
    intake.value = choice
    answered.value = true
  }

  // The type's program, kept for the two births that are SOLD WITHOUT PAPER:
  // a shop that does not quote (sold by definition), and « Vendu » answered
  // « Sans soumission » — nothing ever approves such a job, so its rows are
  // born with it (undated ⇒ the bank). Every other sold birth gets its rows
  // from approval's seeding, never from here.
  const visits = ref<VisitRow[]>([])
  const templateName = ref('')
  // The season line: the type's served default, re-read on every type change
  // (the default is the TYPE's, not the dispatcher's last choice), flipped by
  // hand through `setSeason`.
  const season = ref<IntakeSeason | null>(null)

  // Door 2's provenance: the guard's « Nouveau job lié » stamps the lead job
  // it found here as the PARENT; door 1 (the spawn doors) arrives as the
  // originJobId prefill. Either way the chain records « created from ».
  const branchOriginJobId = ref<string | null>(null)

  // A parent stamped for one address must NOT survive picking another —
  // the origin would silently ride onto an unrelated customer's job.
  watch(guardLocationId, () => { branchOriginJobId.value = null })

  const effectiveOriginJobId = computed(() => prefills.originJobId ?? branchOriginJobId.value ?? null)

  const onBranch = (jobId: string) => {
    branchOriginJobId.value = jobId
  }

  // Picking a job type: the name (the derived title reads it), the program
  // rows (the sales-off path), and THE CARD — the type's flag picks it on
  // every change while the card is still unanswered, never over an answer.
  // Clearing the type (`null`) keeps the rows and the card: both are the
  // dispatcher's by then.
  const seedFromTemplate = (steps: TemplateStep[], name: string, evaluationByDefault?: boolean | null, defaultSeason?: IntakeSeason | null) => {
    templateName.value = name
    season.value = defaultSeason ?? null
    if (!name && !steps.length) return
    visits.value = steps.map(rowFromStep)
    if (evaluationByDefault !== undefined && !answered.value) intake.value = defaultIntakeFor(evaluationByDefault)
  }

  const setSeason = (choice: IntakeSeason | null) => { season.value = choice }

  // The season pair the POST carries — both keys or neither.
  const seasonAttributes = (): Pick<IntakeCreateAttributes, 'work_period_id' | 'season_year'> =>
    season.value ? { work_period_id: season.value.workPeriodId, season_year: season.value.seasonYear } : {}

  // The program as visit specs — undated, so the rows land in the bank.
  // Null when the type has no program: the server then
  // seeds its single pending visit.
  const programVisits = (): NonNullable<CreateInput['visits']> | null => {
    if (!visits.value.length) return null
    return visits.value.map((row, i) => ({
      name: row.name || null,
      kind: row.kind,
      scheduled_at: null,
      duration_minutes: row.durationMinutes ?? null,
      interval_min_days: i === 0 ? null : (row.intervalMinDays ?? null),
      interval_max_days: i === 0 ? null : (row.intervalMaxDays ?? null),
      flexible: true,
    }))
  }

  // Sold without paper: skip_proposal + the type's program born with the job
  // (jobs#create seeds one bare work visit when no rows are named, so the
  // rows go only when the type has some).
  const soldWithoutPaper = (): IntakeCreateAttributes => {
    const rows = programVisits()
    return rows ? { skip_proposal: true, visits: rows } : { skip_proposal: true }
  }

  // THE LANE PART OF THE BODY, both surfaces. Every key is one the server
  // already honours (jobs#create): lead_capture, work_scoped, skip_proposal, a
  // client-named visits[], and the win's won_line / won_template. Nothing here
  // is new to the API.
  const createAttributes = (salesEnabled: boolean, agreement?: IntakeAgreement): IntakeCreateAttributes => {
    // A shop that does not quote has ONE lane: sold work billed on actuals
    // (« Sans soumission ») — the program is born with the job.
    const pair = seasonAttributes()
    if (!salesEnabled) return { ...soldWithoutPaper(), ...pair }
    switch (intake.value) {
      case 'lead':
        return { lead_capture: true, ...pair }
      case 'evaluation':
        return {
          visits: [ {
            name: null, kind: 'evaluation', scheduled_at: null, duration_minutes: null,
            interval_min_days: null, interval_max_days: null, flexible: true,
          } ],
          ...pair,
        }
      case 'quote':
        // Born assessed, no visit — the paper is the next act, on the page.
        return { work_scoped: true, ...pair }
      case 'won': {
        // Born WON. The money answer rides the POST through the one derivation
        // every win door spells (lib/commitment): « Sans soumission » is the
        // paperless birth above; a priced answer is work_scoped (no rows —
        // approval's seeding draws the program) + the accepted paper, minted
        // and approved server-side in the same transaction.
        // The form's validation guarantees an answer; a missing one here is
        // a caller bug and lands as a priced "0" the server refuses loudly.
        const { skip_proposal, ...paper } = commitmentAttributes(
          agreement?.draft ?? { mode: 'priced' },
          agreement?.templateLines ?? [],
          agreement?.proposalTemplateId ?? null,
        )
        return skip_proposal
          ? { ...soldWithoutPaper(), ...pair }
          : { work_scoped: true, ...paper, ...pair }
      }
    }
  }

  const resetIntake = () => {
    intake.value = prefills.initialIntake ?? 'evaluation'
    answered.value = prefills.initialIntake !== undefined
    visits.value = []
    templateName.value = ''
    season.value = null
    branchOriginJobId.value = null
  }

  return {
    intake, templateName, season,
    branchOriginJobId, effectiveOriginJobId,
    onBranch, pickIntake, seedFromTemplate, setSeason, createAttributes, resetIntake,
  }
}
